import React, { useEffect, useState } from 'react';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import AgentLayout from './AgentLayout';
import { Users, ShoppingBag, Wallet, TrendingUp, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Card = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className={`bg-white rounded-2xl shadow-sm p-6 border-l-4 ${color}`} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="mt-2 text-3xl font-bold text-gray-900 tabular-nums">{value}</p>
        {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
      </div>
      <div className="p-3 rounded-lg bg-purple-50">
        <Icon className="w-5 h-5 text-purple-600" />
      </div>
    </div>
  </div>
);

const AgentDashboard = () => {
  const { agentToken, agentUser } = useAgentAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agentToken) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/agent/dashboard`, { headers: { Authorization: `Bearer ${agentToken}` } });
        const j = await res.json();
        setData(j.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [agentToken]);

  const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Ciao {agentUser?.firstName}!</h1>
        <p className="text-sm text-gray-600 mt-1">Ecco un riepilogo del tuo portafoglio.</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl h-32 animate-pulse" />
          ))}
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={Users} label="Clienti totali" value={data.customers?.total || 0}
            sub={`${data.customers?.leads || 0} lead • ${data.customers?.active || 0} attivi`}
            color="border-purple-500" testid="stat-customers" />
          <Card icon={ShoppingBag} label="Ordini aperti" value={data.orders?.open || 0}
            sub="In lavorazione" color="border-blue-500" testid="stat-open-orders" />
          <Card icon={TrendingUp} label="Provvigioni maturate" value={eur(data.commissions?.pending_amount)}
            sub={`${data.commissions?.pending_count || 0} record`} color="border-amber-500" testid="stat-accrued" />
          <Card icon={CheckCircle2} label="Provvigioni pagate" value={eur(data.commissions?.paid_amount)}
            sub={`${data.commissions?.paid_count || 0} record`} color="border-green-500" testid="stat-paid" />
        </div>
      ) : (
        <div className="text-gray-500 text-sm">Nessun dato disponibile</div>
      )}

      <div className="mt-10 bg-white rounded-2xl border border-purple-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Prossimi passi</h2>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2" />
            Visualizza <a href="/agent/customers" className="text-purple-600 hover:underline font-medium">i tuoi clienti</a> per aggiornare stato e note.
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2" />
            Controlla <a href="/agent/quotes" className="text-purple-600 hover:underline font-medium">i preventivi in corso</a> per un follow-up mirato.
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2" />
            Traccia le <a href="/agent/commissions" className="text-purple-600 hover:underline font-medium">tue provvigioni</a> maturate e pagate.
          </li>
        </ul>
      </div>
    </AgentLayout>
  );
};

export default AgentDashboard;
